// mantine
import { Alert, Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// components
import { ResponsiveButton } from '@newo/ui-kit';
// icons
import { Upload } from 'lucide-react';
// utils
import type { TCustomerSessionReportParams } from '@newo/api/gateway';
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';
import { TSessionFilters } from '@/pages/Sessions/Sessions.types';
import { useAccountQuery } from '@/services/customer/account/account.queries.ts';
import { useSendSessionReportToEmailMutation } from '@/services/customer/customer/customer.queries.ts';
import ExportSessionsForm from '@/pages/Sessions/components/ExportSessionsButton/components/ExportSessionForm/ExportSessionForm.tsx';

interface ExportSessionsButtonProps {
    filters: TSessionFilters;
    total: number;
}

const ExportSessionsButton = ({
    filters,
    total
}: ExportSessionsButtonProps) => {
    const [isOpened, handler] = useDisclosure(false);
    const { data: profile } = useProfileQuery();
    const { data: account } = useAccountQuery();

    const { mutateAsync: sendSessionReportToEmail } =
        useSendSessionReportToEmailMutation();

    const handleOpen = () => {
        handler.open();
    };

    const handleClose = () => {
        handler.close();
    };

    const handleSubmit = async (email: string) => {
        const data: TCustomerSessionReportParams = {
            email,
            from_datetime: filters.from_datetime,
            to_datetime: filters.to_datetime,
            tenant: profile?.tenant
        };

        return sendSessionReportToEmail({
            data
        });
    };

    return (
        <>
            <Drawer
                opened={isOpened}
                position="right"
                size="sm"
                title="Export sessions"
                onClose={handleClose}
            >
                <div className="mb-6 text-gray-6">
                    For the selected period you have{' '}
                    <span className="font-bold text-black">{total}</span>{' '}
                    sessions.
                </div>
                <div className="mb-6">
                    {filters.from_datetime ? undefined : (
                        <Alert color="yellow" title="Warning">
                            Please specify a date range in filters to export
                            sessions otherwise the process may take a long time.
                        </Alert>
                    )}
                </div>
                <ExportSessionsForm
                    email={account?.email ?? ''}
                    onSubmit={handleSubmit}
                />
            </Drawer>
            <ResponsiveButton
                icon={<Upload />}
                isDisabled={total === 0}
                onClick={handleOpen}
            >
                Export .csv
            </ResponsiveButton>
        </>
    );
};

export default ExportSessionsButton;
